import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Check, X, Upload, Link as LinkIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAdminAuth } from "@/context/Admin";
import { toast } from "sonner";

export default function MediaSelectorModal({
  isOpen,
  onClose,
  onSelect,
  multiple = true,
  fileTypes = ["image"],
  title = "Select Images",
  description = "Choose one or more images from your media library",
}) {
  const { getFiles, uploadFiles, uploadFileFromUrl } = useAdminAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showUrlUpload, setShowUrlUpload] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchFiles();
    } else {
      setSelectedFiles([]);
      setSearchTerm("");
      setActiveTab("all");
    }
  }, [isOpen]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const result = await getFiles();
      // getFiles returns { files: [...], pagination: {...} }
      setFiles(Array.isArray(result?.files) ? result.files : []);
    } catch (error) {
      console.error("Error fetching files:", error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredFiles = (Array.isArray(files) ? files : []).filter((file) => {
    const matchesSearch = file.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesType = fileTypes.includes(file.type);
    const matchesTab = activeTab === "all" || file.type === activeTab;
    return matchesSearch && matchesType && matchesTab;
  });

  const handleFileClick = (file) => {
    if (multiple) {
      setSelectedFiles((prev) =>
        prev.some((f) => f.id === file.id)
          ? prev.filter((f) => f.id !== file.id)
          : [...prev, file]
      );
    } else {
      setSelectedFiles([file]);
    }
  };

  const handleConfirm = () => {
    if (multiple) {
      onSelect(selectedFiles);
    } else {
      onSelect(selectedFiles[0] || null);
    }
    onClose();
  };

  const handleFileInputChange = async (e) => {
    const filesToUpload = Array.from(e.target.files || []);
    if (filesToUpload.length === 0) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      filesToUpload.forEach((file) => formData.append("files", file));

      await uploadFiles(formData);
      // Refresh the file list after upload
      await fetchFiles();
      // Clear the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleUrlUpload = async () => {
    if (!urlInput.trim()) {
      toast.error("Please enter a valid URL");
      return;
    }

    setIsUploading(true);
    try {
      await uploadFileFromUrl(urlInput);
      setUrlInput("");
      setShowUrlUpload(false);
      // Refresh the file list after upload
      await fetchFiles();
    } catch (error) {
      console.error("Error uploading from URL:", error);
    } finally {
      setIsUploading(false);
    }
  };

  const isFileSelected = (fileId) => selectedFiles.some((f) => f.id === fileId);
  const availableTypes = [
    ...new Set(
      (Array.isArray(files) ? files : [])
        .filter((f) => fileTypes.includes(f.type))
        .map((f) => f.type)
    ),
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!w-screen !max-w-screen !h-screen flex flex-col p-4 lg:p-6">
        {/* Header */}
        <DialogHeader className="pb-3">
          <DialogTitle className="text-lg sm:text-xl">{title}</DialogTitle>
          <DialogDescription className="text-sm sm:text-base">
            {description}
          </DialogDescription>
        </DialogHeader>

        {/* Search + Tabs + Upload */}
        <div className="flex flex-col gap-3 mb-4">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search files..."
                className="pl-8 h-9 sm:h-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Upload Buttons */}
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={fileTypes.includes("image") ? "image/*" : "*/*"}
                onChange={handleFileInputChange}
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="h-9 sm:h-10 text-xs sm:text-sm"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUrlUpload(!showUrlUpload)}
                disabled={isUploading}
                className="h-9 sm:h-10 text-xs sm:text-sm"
              >
                <LinkIcon className="h-4 w-4 mr-2" />
                URL
              </Button>
            </div>

            {/* File Type Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="min-w-max">
                <TabsTrigger value="all" className="text-xs sm:text-sm px-3">
                  All
                </TabsTrigger>
                {availableTypes.map((type) => (
                  <TabsTrigger
                    key={type}
                    value={type}
                    className="capitalize text-xs sm:text-sm px-3"
                  >
                    {type}s
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>

          {/* URL Upload Input */}
          {showUrlUpload && (
            <div className="flex gap-2 items-center">
              <Input
                type="url"
                placeholder="Enter image URL..."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleUrlUpload();
                  }
                }}
                disabled={isUploading}
                className="h-9 sm:h-10 text-sm"
              />
              <Button
                onClick={handleUrlUpload}
                disabled={isUploading || !urlInput.trim()}
                size="sm"
                className="h-9 sm:h-10 text-xs sm:text-sm"
              >
                {isUploading ? "Uploading..." : "Upload"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowUrlUpload(false);
                  setUrlInput("");
                }}
                className="h-9 sm:h-10"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Two Panel Layout */}
        <div className="flex flex-1 min-h-0 gap-4 overflow-hidden">
          {/* Left: Thumbnail grid */}
          <div className="flex-[2] min-h-0">
            <ScrollArea className="h-full scrollbar-hide">
              {loading || isUploading ? (
                <div className="text-center py-8 text-muted-foreground">
                  {isUploading ? "Uploading files..." : "Loading files..."}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 pr-2 ">
                  {filteredFiles.length > 0 ? (
                    filteredFiles.map((file) => (
                      <div
                        key={file.id}
                        onClick={() => handleFileClick(file)}
                        className={`group relative cursor-pointer overflow-hidden aspect-square rounded border bg-background hover:shadow-lg transition-all active:scale-95 ${
                          isFileSelected(file.id)
                            ? "ring-2 ring-primary bg-primary/5"
                            : ""
                        }`}
                      >
                        {isFileSelected(file.id) && (
                          <div className="absolute top-1 right-1 z-10 rounded-full bg-primary p-1">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        )}
                        {file.type === "image" ? (
                          <img
                            src={file.url}
                            alt={file.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-xs sm:text-sm text-muted-foreground">
                            {file.type.toUpperCase()}
                          </div>
                        )}
                        <Badge
                          variant="secondary"
                          className="absolute bottom-1 right-1 text-[10px] capitalize px-1"
                        >
                          {file.type}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-center text-muted-foreground py-8">
                      No files found matching your criteria.
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Right: File details panel */}
          <div className="flex-[1] border-l pl-4 overflow-auto min-h-0 scrollbar-hide">
            {selectedFiles.length === 1 ? (
              <div className="space-y-3">
                <div className="w-full aspect-square overflow-hidden rounded border">
                  {selectedFiles[0].type === "image" ? (
                    <img
                      src={selectedFiles[0].url}
                      alt={selectedFiles[0].name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                      {selectedFiles[0].type.toUpperCase()}
                    </div>
                  )}
                </div>
                <div>
                  <p className="font-medium">{selectedFiles[0].name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedFiles[0].size}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">
                Select a single file to see details here.
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-3 border-t mt-4">
          <div className="flex-1 text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
            {selectedFiles.length > 0 && (
              <span>
                {selectedFiles.length} file
                {selectedFiles.length > 1 ? "s" : ""} selected
              </span>
            )}
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none h-9 sm:h-10 text-sm bg-transparent"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedFiles.length === 0}
              className="flex-1 sm:flex-none h-9 sm:h-10 text-sm"
            >
              <Check className="mr-2 h-4 w-4" />
              Select{" "}
              {multiple && selectedFiles.length > 1
                ? `(${selectedFiles.length})`
                : ""}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
